import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff, Clock, Send, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReminderSettings = ({ token }) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(true);
  const [time1, setTime1] = useState('14:00');
  const [time2, setTime2] = useState('19:00');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testSent, setTestSent] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/workout-reminders/settings`, { headers });
      setEnabled(res.data.enabled);
      setTime1(res.data.reminder_time_1);
      setTime2(res.data.reminder_time_2);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/workout-reminders/settings`, {
        enabled, reminder_time_1: time1, reminder_time_2: time2
      }, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    try {
      await axios.post(`${API}/workout-reminders/send-test`, {}, { headers });
      setTestSent(true);
      setTimeout(() => setTestSent(false), 3000);
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3"></div>
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm" data-testid="reminder-settings">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Bell size={18} className="text-[#FF6600]" />
          Workout Reminders
        </h3>
        <button onClick={() => { setEnabled(!enabled); }}
          className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-[#FF6600]' : 'bg-gray-300 dark:bg-gray-600'}`}
          data-testid="reminder-toggle">
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white dark:bg-gray-800 shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
        </button>
      </div>

      {!enabled && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
          <BellOff size={16} />
          <span>Workout reminders are off. You won't receive daily nudges.</span>
        </div>
      )}

      {enabled && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Get smart reminders if you haven't worked out yet. Up to 2 per day — afternoon and evening.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-1">
                <Clock size={12} className="inline mr-1" /> Afternoon Reminder
              </label>
              <input type="time" value={time1} onChange={e => setTime1(e.target.value)}
                className="w-full p-2.5 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                data-testid="time-1-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-1">
                <Clock size={12} className="inline mr-1" /> Evening Reminder
              </label>
              <input type="time" value={time2} onChange={e => setTime2(e.target.value)}
                className="w-full p-2.5 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                data-testid="time-2-input" />
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={saveSettings} disabled={saving}
              className="flex-1 bg-[#FF6600] text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="save-reminder-btn">
              {saved ? <><Check size={14} /> Saved!</> : saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button onClick={sendTest}
              className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-1"
              data-testid="test-reminder-btn">
              <Send size={14} /> {testSent ? 'Sent!' : 'Test'}
            </button>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/10 p-3 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-[#FF6600]">Smart messages</span> — reminders adapt based on your streak, upcoming badges, personal records, and goals. No generic spam.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReminderSettings;
